import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const CONTACT_INFO = [
  { icon: 'ri-map-pin-line', label: 'Dirección', value: 'C/ Zurbano 96, 6º Derecha, 28003 Madrid' },
  { icon: 'ri-phone-line', label: 'Teléfono', value: '(+34) 696 342 047' },
  { icon: 'ri-mail-line', label: 'Email', value: 'jlarrache@mediatioglobal.com' },
  { icon: 'ri-time-line', label: 'Horario', value: 'Lun–Vie: 9:00 – 20:00' },
];

const SERVICES_SELECT = [
  'Mediación Familiar',
  'Mediación Civil y Mercantil',
  'Mediación Laboral',
  'Mediación Bancaria y Financiera',
  'Mediación Escolar',
  'Mediación Comunitaria',
  'Otro',
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const { ref, isVisible } = useScrollReveal<HTMLElement>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.querySelector('textarea[name="message"]') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;
    setLoading(true);
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value as string));
    try {
      await fetch('https://readdy.ai/api/form/d70nnipqa83q0hmd1djg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={ref} id="contacto" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`text-center mb-14 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: isVisible ? '100ms' : '0ms' }}
        >
          <span className="text-[#3D9E8C] text-sm font-semibold tracking-widest uppercase">Contacto</span>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold text-[#1B3A5C] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Hablemos de tu caso
          </h2>
          <p className="mt-4 text-gray-500 text-[15px] max-w-lg mx-auto">
            Primera consulta completamente gratuita y sin compromiso. Te respondemos en menos de 24 horas.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info + Map */}
          <div
            className={`flex flex-col gap-8 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: isVisible ? '250ms' : '0ms' }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CONTACT_INFO.map((c) => (
                <div key={c.label} className="flex items-start gap-4 p-5 bg-[#F4F7FA] rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#E8F4F1] flex-shrink-0">
                    <i className={`${c.icon} text-[#3D9E8C] text-lg`} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs font-medium">{c.label}</p>
                    <p className="text-[#1B3A5C] text-sm font-semibold mt-0.5">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="w-full h-72 rounded-2xl overflow-hidden">
              <iframe
                title="Ubicación Mediatio Global"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3036.7!2d-3.6957!3d40.4297!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd42287d0f5e6e45%3A0x5b3b2a9a0b8e1c2d!2sCalle%20de%20Zurbano%2C%2096%2C%2028003%20Madrid!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div
            className={`bg-[#F4F7FA] rounded-2xl p-8 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: isVisible ? '400ms' : '0ms' }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#E8F4F1]">
                  <i className="ri-check-double-line text-[#3D9E8C] text-3xl" />
                </div>
                <h3 className="font-bold text-[#1B3A5C] text-xl">¡Mensaje enviado!</h3>
                <p className="text-gray-500 text-sm text-center max-w-xs">
                  Gracias por contactarnos. Te responderemos en menos de 24 horas laborables.
                </p>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[#1B3A5C] text-xs font-semibold">Nombre *</label>
                    <input
                      name="name"
                      type="text"
                      required
                      placeholder="Tu nombre completo"
                      className="px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#3D9E8C] transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[#1B3A5C] text-xs font-semibold">Teléfono</label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="+34 600 000 000"
                      className="px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#3D9E8C] transition-colors"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#1B3A5C] text-xs font-semibold">Correo electrónico *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="tu@email.com"
                    className="px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#3D9E8C] transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#1B3A5C] text-xs font-semibold">Tipo de mediación</label>
                  <select
                    name="service"
                    className="px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-[#3D9E8C] transition-colors cursor-pointer"
                  >
                    <option value="">Selecciona un servicio</option>
                    {SERVICES_SELECT.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#1B3A5C] text-xs font-semibold">
                    Cuéntanos tu caso * <span className="text-gray-400 font-normal">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    placeholder="Describe brevemente tu situación..."
                    className="px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#3D9E8C] transition-colors resize-none"
                  />
                  {charCount > 500 && (
                    <span className="text-red-500 text-xs">Has superado el límite de 500 caracteres.</span>
                  )}
                </div>
                <div className="flex items-start gap-3">
                  <input
                    name="privacy"
                    type="checkbox"
                    required
                    id="privacy"
                    className="mt-0.5 cursor-pointer"
                  />
                  <label htmlFor="privacy" className="text-gray-500 text-xs leading-relaxed cursor-pointer">
                    He leído y acepto la <a href="/politica-de-privacidad" className="text-[#3D9E8C] underline">Política de Privacidad</a>. Consiento el tratamiento de mis datos para atender mi consulta.
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="w-full py-4 bg-[#1B3A5C] text-white text-sm font-semibold rounded-full hover:bg-[#14304f] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {loading ? 'Enviando...' : 'Enviar Consulta Gratuita'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
